import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { isKRSuperAdmin } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Eye, LogOut, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ImpersonationSearchDialog } from './ImpersonationSearchDialog';

/**
 * ImpersonationBar — renders one of three states:
 *  1. Currently impersonating → sticky amber warning banner with Exit button (anyone)
 *  2. SuperAdmin, not impersonating → compact "View As" launcher (top-right floating)
 *  3. Everyone else → nothing
 */
export const ImpersonationBar = () => {
  const { user, isImpersonating, exitImpersonation } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [exiting, setExiting] = useState(false);

  const handleExit = async () => {
    setExiting(true);
    try {
      await exitImpersonation();
      toast.success('Back to your account');
      window.location.href = '/dashboard';
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to exit impersonation';
      toast.error(typeof msg === 'string' ? msg : 'Failed to exit impersonation');
      setExiting(false);
    }
  };

  // State 1: Impersonating — show the banner regardless of role
  if (isImpersonating) {
    const imp = user?.impersonation || {};
    return (
      <div
        className="impersonation-banner sticky top-0 z-40 w-full bg-amber-500 text-amber-950 shadow-md"
        data-testid="impersonation-banner"
      >
        <div className="flex items-center justify-between gap-3 px-4 py-2 max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <div className="text-sm min-w-0">
              <span className="font-semibold uppercase tracking-wide mr-2">
                Impersonation Mode
              </span>
              <span className="truncate">
                Viewing as <strong>{imp.target_label || user?.name}</strong>
                {imp.target_kind ? ` (${imp.target_kind})` : ''} · Real account:{' '}
                <strong>{imp.by_email}</strong>
              </span>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="bg-white hover:bg-amber-50 border-amber-700 text-amber-900 flex-shrink-0"
            onClick={handleExit}
            disabled={exiting}
            data-testid="exit-impersonation-btn"
          >
            {exiting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-1" /> Exiting...
              </>
            ) : (
              <>
                <LogOut className="w-4 h-4 mr-1" /> Exit Impersonation
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // State 2: SuperAdmin not impersonating — floating launcher
  if (!isKRSuperAdmin(user)) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setSearchOpen(true)}
        className="fixed bottom-4 right-4 z-40 hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 text-amber-950 text-[11px] font-semibold shadow-lg hover:bg-amber-400 transition-colors ring-2 ring-amber-300/50"
        title="View the portal as an agent or agency"
        data-testid="view-as-btn"
      >
        <Eye className="w-3.5 h-3.5" />
        View as...
      </button>
      <ImpersonationSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
};

export default ImpersonationBar;
